import {
  AvailabilityTimeFrame,
  deriveBookableDurations,
  deriveBookableStartTimes,
  deriveDayBookingSpanFromWindows,
  isContinuousScheduleType,
  isDayBasedTimeFrame,
  type AvailabilityRule,
  type ScheduleType,
  type OperatingHoursWindow,
} from '@kouto/types';
import { type Locale } from 'date-fns';
import { Session } from 'types/listings';
import addDays from 'utils/date/addDays';
import daysBetween from 'utils/date/daysBetween';
import { MINUTES_PER_DAY } from 'utils/date/minutesPerDay';
import toMinutesOfDay from 'utils/date/toMinutesOfDay';
import formatMinutes from 'utils/duration/formatMinutes';

/** The longest day-based booking the picker offers when the rule sets no maximum. */
const DEFAULT_MAX_DAY_UNITS = 30;

/** The plugin's URL and cart carry times as "HH:mm:ss"; the shared helpers hand back "HH:mm". */
const toUrlTime = (time: string): string =>
  time.length === 5 ? `${time}:00` : time;

/**
 * A listing is booked continuously when its collection is flexible or multi-day rather than scheduled:
 * the guest chooses a start and a length rather than one of a set of sessions.
 */
export const isContinuousListing = (
  listing: { scheduleType?: ScheduleType | null } | undefined | null,
): boolean => isContinuousScheduleType(listing?.scheduleType);

// Daily-or-nightly is a question about a group's rule, so the shared guard answers it; re-exported so the
// picker, the pages and the hooks keep one import path for everything about a continuous selection.
export { isDayBasedTimeFrame };

/**
 * The open spans among what the sessions endpoint returned: the rows a continuously booked resource
 * exposes instead of sessions. A span too short for the shortest booking is not offered.
 */
export const selectOpenSpans = (sessions: readonly Session[]): Session[] =>
  sessions.filter(
    (session) =>
      session.isContinuousAvailability === true &&
      session.isBookableAvailability !== false,
  );

/** A span, as minutes from midnight of its own date, for arithmetic against a chosen time. */
interface SpanBounds {
  readonly date: string;
  readonly end: number;
  readonly experienceId: string;
  readonly session: Session;
  readonly start: number;
}

const toSpanBounds = (session: Session): SpanBounds => {
  const [date] = session.startDateTime.split('T');
  const start = toMinutesOfDay(session.startDateTime);
  return {
    date,
    end: start + session.duration,
    experienceId: session.experienceId,
    session,
    start,
  };
};

const spansOn = (
  spans: readonly Session[],
  date: string,
  experienceId?: string,
): SpanBounds[] =>
  spans
    .filter((span) => span.startDateTime.startsWith(date))
    .filter((span) => !experienceId || span.experienceId === experienceId)
    .map(toSpanBounds);

/**
 * The spans, one per resource, that hold a booking from `start` for `durationMinutes` on `date`.
 * Empty when nothing is free for the whole of it.
 */
const spansHolding = (
  spans: readonly Session[],
  date: string,
  start: number,
  durationMinutes: number,
  experienceId?: string,
): Session[] =>
  spansOn(spans, date, experienceId)
    .filter(
      (span) => span.start <= start && start + durationMinutes <= span.end,
    )
    .map((span) => span.session);

/**
 * The start times a flexible booking may begin at on a date: every start the operating hours allow
 * that also falls inside a free span with room for the shortest booking.
 */
export const deriveFlexibleStartOptions = ({
  date,
  experienceId,
  rule,
  spans,
}: {
  date: string;
  experienceId?: string;
  rule: AvailabilityRule;
  spans: readonly Session[];
}): string[] =>
  deriveBookableStartTimes({
    bookingIncrementMinutes: rule.bookingIncrementMinutes,
    date,
    experienceId,
    minDurationMinutes: rule.minDurationMinutes,
    windows: rule.operatingHours,
  })
    .map(toUrlTime)
    .filter(
      (startTime) =>
        spansHolding(
          spans,
          date,
          toMinutesOfDay(`${date}T${startTime}`),
          rule.minDurationMinutes,
          experienceId,
        ).length > 0,
    );

/**
 * The lengths a flexible booking may run from a chosen start: the rule's ladder, cut where the free
 * span the start sits in ends.
 */
export const deriveFlexibleDurationOptions = ({
  date,
  durations,
  experienceId,
  rule,
  spans,
  startTime,
}: {
  date: string;
  /** The set lengths the group is sold in; when given, these are the only lengths on offer. */
  durations?: readonly { durationMinutes: number }[];
  experienceId?: string;
  rule: AvailabilityRule;
  spans: readonly Session[];
  startTime: string;
}): number[] => {
  const start = toMinutesOfDay(`${date}T${startTime}`);
  const fits = (durationMinutes: number) =>
    spansHolding(spans, date, start, durationMinutes, experienceId).length > 0;
  const setLengths = Array.from(
    new Set((durations ?? []).map((d) => d.durationMinutes)),
  ).sort((a, b) => a - b);
  // An operator who fixed the lengths a resource is sold in means exactly those: a 3-hour rung is a
  // 3-hour booking, not "anything from 1h in 15-minute steps". The rule's frame says which kind of
  // group this is; a frame that says nothing about lengths (older data, a day-based rule handed a
  // duration set) is read the way it always was, set lengths when there are any. A specific-durations
  // group with no lengths configured falls through to the increment ladder rather than offering nothing.
  const sellsSetLengths =
    rule.timeFrame === AvailabilityTimeFrame.SPECIFIC_DURATIONS ||
    (rule.timeFrame !== AvailabilityTimeFrame.FLEXIBLE_DURATIONS &&
      setLengths.length > 0);
  if (sellsSetLengths && setLengths.length > 0) {
    return setLengths.filter(fits);
  }
  return deriveBookableDurations({
    date,
    experienceId,
    rules: rule,
    startTime,
    windows: rule.operatingHours,
  }).filter(fits);
};

/** The dates a day-based resource has a free whole day on, for the picker to walk forward through. */
const isDateFree = (
  spans: readonly Session[],
  date: string,
  experienceId?: string,
): boolean => spansOn(spans, date, experienceId).length > 0;

/**
 * How many days or nights may be booked from a start date: one, two, three... for as long as every
 * date held is free, up to the rule's longest booking. A nightly booking holds the nights, so its
 * end date need not be free; a daily one holds the end day too.
 */
export const deriveDayUnitOptions = ({
  date,
  experienceId,
  rule,
  spans,
}: {
  date: string;
  experienceId?: string;
  rule: AvailabilityRule;
  spans: readonly Session[];
}): number[] => {
  const minUnits = Math.max(
    1,
    Math.round(rule.minDurationMinutes / MINUTES_PER_DAY),
  );
  const maxUnits = rule.maxDurationMinutes
    ? Math.max(minUnits, Math.round(rule.maxDurationMinutes / MINUTES_PER_DAY))
    : DEFAULT_MAX_DAY_UNITS;

  const freeRun = Array.from({ length: maxUnits }, (_, index) =>
    addDays(date, index),
  ).findIndex((held) => !isDateFree(spans, held, experienceId));
  const heldDates = freeRun === -1 ? maxUnits : freeRun;

  return Array.from({ length: maxUnits }, (_, index) => index + 1).filter(
    (units) => units >= minUnits && units <= heldDates,
  );
};

/** The end date for a start date and a count of days or nights. */
export const deriveEndDate = (
  date: string,
  units: number,
  timeFrame: AvailabilityTimeFrame,
): string =>
  timeFrame === AvailabilityTimeFrame.NIGHTLY
    ? addDays(date, units)
    : addDays(date, units - 1);

export interface ContinuousSelection {
  /** Minutes the resource is held, start to end. */
  readonly durationMinutes: number;
  /** For a day-based frame, how many days or nights; for a flexible one, undefined. */
  readonly units?: number;
  /** The free spans, one per resource, the selection fits inside. */
  readonly spans: Session[];
  /** Local "YYYY-MM-DDTHH:mm:ss". */
  readonly startDateTime: string;
}

/**
 * A day-based booking's span from a start date and a count of units, at the operating hours' start
 * and end times.
 */
const deriveDaySelection = ({
  date,
  experienceId,
  spans,
  timeFrame,
  units,
  windows,
}: {
  date: string;
  experienceId?: string;
  spans: readonly Session[];
  timeFrame: AvailabilityTimeFrame;
  units: number;
  windows: readonly OperatingHoursWindow[];
}): ContinuousSelection | null => {
  const span = deriveDayBookingSpanFromWindows({
    experienceId,
    from: date,
    timeFrame,
    to: deriveEndDate(date, units, timeFrame),
    windows,
  });
  const heldDates = Array.from({ length: units }, (_, index) =>
    addDays(date, index),
  );
  // One free span per resource on the first date, kept only when the resource is free every date held.
  const resources = spansOn(spans, date, experienceId).filter((first) =>
    heldDates.every((held) => isDateFree(spans, held, first.experienceId)),
  );

  if (!resources.length) {
    return null;
  }

  return {
    durationMinutes: span.durationMinutes,
    spans: resources.map(({ session }) => session),
    startDateTime: span.startDateTime,
    units,
  };
};

/**
 * What the URL's time and duration mean for a continuously booked resource, or null when they do not
 * describe a booking that is free.
 *
 * Flexible: `time` is the start and `duration` the length, both checked against the free spans. Daily
 * and nightly: `duration` carries the count of units as whole days (two nights travel as PT48H), and
 * the clock times come from the operating hours, so a guest picks dates, never times.
 */
export const buildContinuousSelection = ({
  date,
  duration,
  experienceId,
  rule,
  spans,
  time,
  timeFrame,
  durations,
}: {
  date: string;
  duration?: number;
  experienceId?: string;
  rule: AvailabilityRule;
  spans: readonly Session[];
  time?: string;
  timeFrame: AvailabilityTimeFrame;
  /** The set lengths the group is sold in, when fixed. */
  durations?: readonly { durationMinutes: number }[];
}): ContinuousSelection | null => {
  if (isDayBasedTimeFrame(timeFrame)) {
    const units = duration ? Math.round(duration / MINUTES_PER_DAY) : 0;
    return units >= 1
      ? deriveDaySelection({
          date,
          experienceId,
          spans,
          timeFrame,
          units,
          windows: rule.operatingHours,
        })
      : null;
  }

  if (!time || !duration) {
    return null;
  }
  // Only a length the rule offers from that start: a stale URL from another flow must not book an odd
  // length the picker would never show.
  const offered = deriveFlexibleDurationOptions({
    durations,
    date,
    experienceId,
    rule,
    spans,
    startTime: time,
  });
  if (!offered.includes(duration)) {
    return null;
  }
  const holding = spansHolding(
    spans,
    date,
    toMinutesOfDay(`${date}T${time}`),
    duration,
    experienceId,
  );
  return holding.length
    ? {
        durationMinutes: duration,
        spans: holding,
        startDateTime: `${date}T${time}`,
      }
    : null;
};

/**
 * The selection to offer when the URL names none: the earliest free start at the shortest length for
 * a flexible resource, the shortest stay for a day-based one. Returned as the URL would carry it.
 */
export const deriveDefaultContinuousParams = ({
  date,
  experienceId,
  rule,
  spans,
  timeFrame,
  durations,
}: {
  date: string;
  experienceId?: string;
  rule: AvailabilityRule;
  spans: readonly Session[];
  timeFrame: AvailabilityTimeFrame;
  /** The set lengths the group is sold in, when fixed. */
  durations?: readonly { durationMinutes: number }[];
}): { duration: number; time?: string } | null => {
  if (isDayBasedTimeFrame(timeFrame)) {
    const [units] = deriveDayUnitOptions({ date, experienceId, rule, spans });
    return units ? { duration: units * MINUTES_PER_DAY } : null;
  }

  const [time] = deriveFlexibleStartOptions({
    date,
    experienceId,
    rule,
    spans,
  });
  if (!time) {
    return null;
  }
  const [duration] = deriveFlexibleDurationOptions({
    durations,
    date,
    experienceId,
    rule,
    spans,
    startTime: time,
  });
  return duration ? { duration, time } : null;
};

/**
 * The selection the URL names, when it is still free; else the earliest free one; else none.
 *
 * Both attempts price against the same spans, so a URL carrying a start that has since been taken
 * falls back to a bookable choice rather than leaving the page with nothing selected.
 */
export const deriveContinuousSelection = ({
  duration,
  time,
  ...params
}: {
  date: string;
  duration?: number;
  durations: readonly { durationMinutes: number }[];
  experienceId?: string;
  rule: AvailabilityRule;
  spans: readonly Session[];
  time?: string;
  timeFrame: AvailabilityTimeFrame;
}): ContinuousSelection | null => {
  const fromUrl = buildContinuousSelection({ ...params, duration, time });
  if (fromUrl) {
    return fromUrl;
  }
  const defaults = deriveDefaultContinuousParams(params);
  return defaults ? buildContinuousSelection({ ...params, ...defaults }) : null;
};

/**
 * The selection as the rest of the booking flow expects a session: the free span's row with the chosen
 * start and length in place of the span's own, so tiers, resource and capacity read as they always have.
 */
export const toSelectedSession = (selection: ContinuousSelection): Session => ({
  ...selection.spans[0],
  duration: selection.durationMinutes,
  startDateTime: selection.startDateTime,
});

/**
 * How many days or nights a picked range buys: a nightly stay counts the nights between the dates, a
 * daily rental counts every date including the end day. Null when the range buys nothing.
 */
export const deriveUnitsFromRange = ({
  from,
  timeFrame,
  to,
}: {
  from: string;
  timeFrame: AvailabilityTimeFrame;
  to: string;
}): number | null => {
  const days = daysBetween(from, to);
  const units = timeFrame === AvailabilityTimeFrame.NIGHTLY ? days : days + 1;
  return units >= 1 ? units : null;
};

/** The start and end dates a selection of `units` from `date` occupies, as the calendar shows them. */
export const deriveRangeFromUnits = ({
  date,
  timeFrame,
  units,
}: {
  date: string;
  timeFrame: AvailabilityTimeFrame;
  units: number;
}): { from: string; to: string } => ({
  from: date,
  to: deriveEndDate(date, units, timeFrame),
});

/**
 * Whether a stay from `from` to `to` could be booked: no longer than the rule's longest stay, and every
 * date it holds still free. A nightly stay holds the nights up to the one before the end date; a daily
 * rental holds every date through the end date.
 */
export const isStayRangeBookable = ({
  availableDates,
  from,
  maxUnits,
  timeFrame,
  to,
}: {
  availableDates: ReadonlySet<string>;
  from: string;
  maxUnits: number;
  timeFrame: AvailabilityTimeFrame;
  to: string;
}): boolean => {
  const isNightly = timeFrame === AvailabilityTimeFrame.NIGHTLY;
  const span = daysBetween(from, to);
  if (span < (isNightly ? 1 : 0)) {
    return false;
  }
  const units = isNightly ? span : span + 1;
  if (units > maxUnits) {
    return false;
  }
  const lastHeld = isNightly ? span - 1 : span;
  return Array.from({ length: lastHeld + 1 }, (_, index) =>
    addDays(from, index),
  ).every((held) => availableDates.has(held));
};

/**
 * Whether a calendar date may be picked for a stay.
 *
 * Before a start date is picked, any future date with a bookable span is fair game. Once one is
 * picked, a later date must make a bookable stay from the start date, and an earlier date must make one
 * ending on it — the picker swaps the two — so the longest stay and the booked-out dates bind in both
 * directions.
 */
export const isStayDateSelectable = ({
  availableDates,
  date,
  from,
  maxUnits,
  timeFrame,
  today,
}: {
  availableDates: ReadonlySet<string>;
  date: string;
  from: string | null;
  maxUnits: number;
  timeFrame: AvailabilityTimeFrame;
  today: string;
}): boolean => {
  if (date < today) {
    return false;
  }
  if (!from || date === from) {
    return availableDates.has(date);
  }
  const [start, end] = date < from ? [date, from] : [from, date];
  return isStayRangeBookable({
    availableDates,
    from: start,
    maxUnits,
    timeFrame,
    to: end,
  });
};

/** The rule's longest stay in days or nights, or the picker's own ceiling when it sets none. */
export const deriveMaxUnits = (rule: AvailabilityRule): number =>
  rule.maxDurationMinutes
    ? Math.max(1, Math.round(rule.maxDurationMinutes / MINUTES_PER_DAY))
    : DEFAULT_MAX_DAY_UNITS;

/**
 * The translation key for a count of booked units, in the frame's own word: "night"/"nights" for a
 * nightly stay, "day"/"days" for a daily one, "hour"/"hours" for anything booked by the clock.
 *
 * A null frame reads as hourly: a group with no day-based rule is booked by the clock, and that is
 * also the safe reading for a cart line whose listing has not resolved a frame yet.
 */
export const deriveUnitLabelKey = (
  timeFrame: AvailabilityTimeFrame | null,
  units: number,
): string => {
  const isSingular = units === 1;
  if (timeFrame === AvailabilityTimeFrame.NIGHTLY) {
    return isSingular ? 'night' : 'nights';
  }
  if (timeFrame === AvailabilityTimeFrame.DAILY) {
    return isSingular ? 'day' : 'days';
  }
  return isSingular ? 'hour' : 'hours';
};

/** "2 nights", "3 days" or "2 hr 30 min": how long a picked selection runs, in the frame's own words. */
export const describeStayLength = (
  selection: ContinuousSelection,
  timeFrame: AvailabilityTimeFrame,
  translate: (key: string) => string,
  locale?: Locale,
): string => {
  if (!isDayBasedTimeFrame(timeFrame)) {
    return formatMinutes(selection.durationMinutes, locale);
  }
  const units = selection.units ?? 1;
  return `${units} ${translate(deriveUnitLabelKey(timeFrame, units))}`;
};
